// Create-key Drawer (api-keys-settings.md, List archetype §4c). The Drawer —
// not a Popover — is deliberate: the flow is deep-linked via `?new=1`, embeds
// a paged agent select with Load-more, and renders inline create errors. Field
// values are never URL state: the form (and its state) mounts only while the
// drawer is open, so `?new=1` on refresh reopens an empty drawer.

import { useState, type SyntheticEvent } from "react";

import type { components } from "../api/schema";
import {
  Button,
  Drawer,
  Inline,
  InlineError,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from "../ui";
import { ApiKeyScopeSelector } from "./ApiKeyScopeSelector";

type Agent = components["schemas"]["AgentResponse"];
type Team = components["schemas"]["TeamResponse"];
type ScopeCatalog = components["schemas"]["ApiKeyScopeCatalogResponse"];

type ScopeSelection = "agent" | "org" | "team";

export interface ApiKeyCatalogTarget {
  agentId?: string;
  teamId?: string;
}

export interface ApiKeyCreateInput {
  agentId: string | null;
  teamId: string | null;
  displayName: string;
  scopes: string[];
}

export interface ApiKeyCreateDrawerProps {
  agents: Agent[];
  agentsErrorMessage?: string;
  canCreateOrgWide: boolean;
  createErrorMessage?: string;
  hasMoreAgents: boolean;
  hasMoreTeams: boolean;
  isAgentsFetchingNextPage: boolean;
  isAgentsPending: boolean;
  isCreatePending: boolean;
  isTeamsFetchingNextPage: boolean;
  isOpen: boolean;
  isScopeCatalogPending: boolean;
  initialTeamId?: string;
  onClose: () => void;
  onCatalogTargetChange: (
    target: ApiKeyCatalogTarget | null | undefined,
  ) => void;
  onFetchNextAgents: () => void;
  onFetchNextTeams: () => void;
  onSubmit: (input: ApiKeyCreateInput) => void;
  scopeCatalog?: ScopeCatalog;
  scopeCatalogErrorMessage?: string;
  teams: Team[];
  teamsErrorMessage?: string;
}

export function ApiKeyCreateDrawer(props: ApiKeyCreateDrawerProps) {
  return (
    <Drawer
      ariaLabel="Create key"
      isOpen={props.isOpen}
      size="lg"
      onDismiss={props.onClose}
    >
      <ApiKeyCreateForm {...props} />
    </Drawer>
  );
}

// Rendered only while the drawer is open (Drawer returns null when closed),
// so field state resets on every close/reopen by unmounting.
function ApiKeyCreateForm({
  agents,
  agentsErrorMessage,
  canCreateOrgWide,
  createErrorMessage,
  hasMoreAgents,
  hasMoreTeams,
  isAgentsFetchingNextPage,
  isAgentsPending,
  isCreatePending,
  isTeamsFetchingNextPage,
  isScopeCatalogPending,
  initialTeamId,
  onCatalogTargetChange,
  onClose,
  onFetchNextAgents,
  onFetchNextTeams,
  onSubmit,
  scopeCatalog,
  scopeCatalogErrorMessage,
  teams,
  teamsErrorMessage,
}: ApiKeyCreateDrawerProps) {
  const [displayName, setDisplayName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [scopeSelection, setScopeSelection] = useState<ScopeSelection>(
    initialTeamId == null && canCreateOrgWide ? "org" : "team",
  );
  const [selectedTeamId, setSelectedTeamId] = useState(initialTeamId ?? "");
  const [selectedAgentId, setSelectedAgentId] = useState("");

  const createDisabled =
    isCreatePending ||
    isScopeCatalogPending ||
    scopeCatalogErrorMessage != null ||
    scopeCatalog == null ||
    displayName.trim().length === 0 ||
    (scopeSelection === "team" && selectedTeamId.length === 0) ||
    (scopeSelection === "agent" && selectedAgentId.length === 0);

  function handleSubmit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    onSubmit({
      agentId:
        scopeSelection === "agent" && selectedAgentId.length > 0
          ? selectedAgentId
          : null,
      teamId:
        scopeSelection === "team" && selectedTeamId.length > 0
          ? selectedTeamId
          : null,
      displayName: displayName.trim(),
      scopes,
    });
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap={3}>
        <TextInput
          label="Display name"
          required
          value={displayName}
          onChange={(event) => {
            setDisplayName(event.currentTarget.value);
          }}
        />
        <Select
          label="Scope"
          value={scopeSelection}
          onChange={(event) => {
            const next = event.currentTarget.value as ScopeSelection;
            setScopeSelection(next);
            setScopes([]);
            onCatalogTargetChange(
              next === "org"
                ? null
                : next === "team"
                  ? selectedTeamId
                    ? { teamId: selectedTeamId }
                    : undefined
                  : selectedAgentId
                    ? { agentId: selectedAgentId }
                    : undefined,
            );
          }}
        >
          {canCreateOrgWide ? <option value="org">Org-wide</option> : null}
          <option value="team">Team-locked</option>
          <option value="agent">Agent-locked</option>
        </Select>
        {scopeSelection === "team" ? (
          <>
            <Select
              label="Team"
              required
              value={selectedTeamId}
              onChange={(event) => {
                const teamId = event.currentTarget.value;
                setSelectedTeamId(teamId);
                setScopes([]);
                onCatalogTargetChange(
                  teamId.length > 0 ? { teamId } : undefined,
                );
              }}
            >
              <option value="">Choose team</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.display_name}
                </option>
              ))}
            </Select>
            {teamsErrorMessage ? (
              <InlineError>{teamsErrorMessage}</InlineError>
            ) : null}
            {!teamsErrorMessage && teams.length === 0 ? (
              <Text variant="muted">No teams.</Text>
            ) : null}
            {hasMoreTeams ? (
              <Inline>
                <Button
                  disabled={isTeamsFetchingNextPage}
                  size="sm"
                  type="button"
                  variant="secondary"
                  onClick={onFetchNextTeams}
                >
                  {isTeamsFetchingNextPage ? "Loading..." : "Load more teams"}
                </Button>
              </Inline>
            ) : null}
          </>
        ) : null}
        {scopeSelection === "agent" ? (
          <>
            <Select
              label="Agent"
              required
              value={selectedAgentId}
              onChange={(event) => {
                const agentId = event.currentTarget.value;
                setSelectedAgentId(agentId);
                setScopes([]);
                onCatalogTargetChange(
                  agentId.length > 0 ? { agentId } : undefined,
                );
              }}
            >
              <option value="">Choose agent</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.display_name}
                </option>
              ))}
            </Select>
            {isAgentsPending ? (
              <Skeleton label="Loading agents" shape="detail-field" />
            ) : null}
            {agentsErrorMessage ? (
              <InlineError>{agentsErrorMessage}</InlineError>
            ) : null}
            {!isAgentsPending && !agentsErrorMessage && agents.length === 0 ? (
              <Text variant="muted">No agents.</Text>
            ) : null}
            {hasMoreAgents ? (
              <Inline>
                <Button
                  disabled={isAgentsFetchingNextPage}
                  size="sm"
                  type="button"
                  variant="secondary"
                  onClick={onFetchNextAgents}
                >
                  {isAgentsFetchingNextPage ? "Loading..." : "Load more agents"}
                </Button>
              </Inline>
            ) : null}
          </>
        ) : null}
        <ApiKeyScopeSelector
          catalog={scopeCatalog}
          errorMessage={scopeCatalogErrorMessage}
          isPending={isScopeCatalogPending}
          scopes={scopes}
          onChange={setScopes}
        />
        {createErrorMessage ? (
          <InlineError>{createErrorMessage}</InlineError>
        ) : null}
        <Inline gap={3}>
          <Button disabled={createDisabled} type="submit">
            {isCreatePending ? "Creating..." : "Create"}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Inline>
      </Stack>
    </form>
  );
}
